import React from "react";
import { GenericTableColumn } from "@ui-components/Table/GenericTable";
import { GenericTag } from "@ui-components/Tag/GenericTag";

/**
 * Report flavours (CTX-782). `summary` is the short digest (a line per
 * service); `detailed` is that digest plus the per-service sections. The
 * monthly job emits both for every tenant (CTX-1137).
 *
 * The wire values are the backend's and are not renamed here: `summary` reads
 * as **Executive** in the UI through {@link getReportTab}.
 */
export type ReportType = "summary" | "detailed";

/**
 * Generation lifecycle. The monthly job builds reports in the background, so a
 * row is born `pending` and has to be re-read until it settles.
 *
 * `completed_local` is a third settled state and not a variant of `completed`:
 * the document was built but never reached storage, and the backend refuses to
 * hand back a download URL for it (`download-report/helpers.js` gates on
 * `completed` alone). It is the state every report settles on outside
 * production, where S3 is not configured.
 */
export type ReportStatus =
  | "pending"
  | "generating"
  | "completed"
  | "completed_local"
  | "failed";

export interface Report {
  id: number;
  tenantId: number;
  customer_id: string;
  customer_name: string;
  date: string;
  reportMonth: number;
  reportYear: number;
  /** Report scope discriminator: Odoo orderId or "default" (main report). */
  reportGroup?: string;
  /** Human-readable label for the group (e.g. "OC-82"); null for the main report. */
  reportGroupLabel?: string | null;
  /**
   * Absent on rows older than CTX-782's column. Those rows list under the
   * Detailed tab — see {@link getReportTab}.
   */
  reportType?: ReportType;
  status: string;
  /** Why a `failed` generation failed. Only populated for that status. */
  errorMessage?: string | null;
  generatedAt: string;
  createdAt: string;
}

/**
 * The page's tabs, one per report type, in display order (CTX-1192). The tab
 * label is the type's UI name, so the tab says what the document is titled.
 * Detailed leads: it is the report the list carried alone until CTX-1137.
 */
export const REPORT_TABS = ["Detailed", "Executive"] as const;
export type ReportTab = (typeof REPORT_TABS)[number];

const REPORT_TAB_BY_TYPE: Record<ReportType, ReportTab> = {
  summary: "Executive",
  detailed: "Detailed",
};

/**
 * The tab a report lists under. A row with no type predates CTX-782, when
 * Detailed was the only report there was — so that is where it belongs, rather
 * than in neither tab.
 */
export function getReportTab(report: Report): ReportTab {
  return REPORT_TAB_BY_TYPE[report.reportType ?? "detailed"] ?? "Detailed";
}

type StatusPresentation = {
  variant: "success" | "warning" | "error" | "neutral";
  label: string;
};

/**
 * Lookup instead of an if-chain so adding a status is one row, and so the
 * in-progress rule below cannot drift from what the tag says.
 */
const STATUS_PRESENTATION: Record<ReportStatus, StatusPresentation> = {
  pending: { variant: "warning", label: "Pending" },
  generating: { variant: "warning", label: "Generating" },
  completed: { variant: "success", label: "Completed" },
  completed_local: { variant: "warning", label: "Not stored" },
  failed: { variant: "error", label: "Failed" },
};

/** Statuses that mean the backend is still working on the file. */
const IN_PROGRESS_STATUSES: ReportStatus[] = ["pending", "generating"];

export function formatReportStatus(status: string): StatusPresentation {
  const known = STATUS_PRESENTATION[status as ReportStatus];
  if (known) return known;
  // Unknown status from the API: show it verbatim rather than swallowing it.
  return {
    variant: "neutral",
    label: status ? status.charAt(0).toUpperCase() + status.slice(1) : "",
  };
}

/** A row the backend is still generating — it will change without user action. */
export function isReportInProgress(report: Report): boolean {
  return IN_PROGRESS_STATUSES.includes(report.status as ReportStatus);
}

/**
 * Single source of truth for "should the list keep re-reading itself".
 * Consumed by the auto-refresh hook and asserted directly in its tests.
 */
export function hasReportInProgress(reports: Report[]): boolean {
  return reports.some(isReportInProgress);
}

/** Only a report whose file reached storage can be downloaded. */
export function isReportDownloadable(report: Report): boolean {
  return report.status === "completed";
}

/** Why a row offers no download, and how loudly to say it. */
export type UndownloadableReason = {
  message: string;
  /**
   * The backend's own words, when they are too technical for the cell but
   * still the thing an operator needs — surfaced on hover. `null` when
   * `message` already is that text.
   */
  detail: string | null;
  tone: "danger" | "muted";
};

/**
 * The text that replaces the download button, or `null` when the button is the
 * right thing to show. Lives next to {@link isReportDownloadable} so the two
 * halves of the same rule — whether there is a file, and what to say when
 * there is not — cannot drift apart.
 *
 * A row still being generated returns `null`: its status tag already says so,
 * and the disabled button is the honest control.
 */
export function getUndownloadableReason(
  report: Report
): UndownloadableReason | null {
  if (report.status === "failed") {
    return {
      message: report.errorMessage || "Generation failed",
      detail: null,
      tone: "danger",
    };
  }
  if (report.status === "completed_local") {
    // The backend states the cause here (an expired credential, a bucket it
    // could not reach). It is infrastructure language, so the cell stays
    // plain and the cause rides along on hover rather than being dropped.
    return {
      message: "Generated, but not available for download",
      detail: report.errorMessage ?? null,
      tone: "muted",
    };
  }
  return null;
}

/** Label shown in the Purchase Order column. The "default" group is the main
 * report (legacy/no-order services); other groups carry their PO label. */
export function formatReportGroup(report: Report): string {
  if (!report.reportGroup || report.reportGroup === "default") {
    return "Main";
  }
  return report.reportGroupLabel || `OC-${report.reportGroup}`;
}

/** Format date as "February, 2026" for display and filtering (year + month name match). */
export function formatDateMonthYear(dateStr: string): string {
  if (!dateStr) return "";
  const datePart = dateStr.split("T")[0];
  const [year, month] = datePart.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, 1));
  const monthName = date.toLocaleDateString("en-US", {
    month: "long",
    timeZone: "UTC",
  });
  return `${monthName}, ${year}`;
}

/** The two orders a phone offers instead of a sort-by-any-column control. */
export const REPORT_PHONE_ORDERS = ["Date", "Customer"] as const;
export type ReportPhoneOrder = (typeof REPORT_PHONE_ORDERS)[number];

const reportPeriod = (report: Report) => report.reportYear * 12 + report.reportMonth;

/**
 * Newest month first; "Customer" groups each customer's reports together,
 * still newest first inside the group (CTX-1383).
 */
export function sortReportsForPhone(
  reports: Report[],
  order: ReportPhoneOrder
): Report[] {
  return [...reports].sort((a, b) => {
    if (order === "Customer") {
      const byName = (a.customer_name ?? "").localeCompare(b.customer_name ?? "");
      if (byName !== 0) return byName;
    }
    return reportPeriod(b) - reportPeriod(a);
  });
}

/**
 * Columns shared by every render of the table. The Actions column is appended
 * by the page, since it closes over the download handler.
 */
export const reportsBaseColumns: GenericTableColumn<Report>[] = [
  {
    header: "ID",
    accessor: "id",
  },
  {
    header: "Customer Name",
    accessor: "customer_name",
  },
  {
    header: "Customer ID",
    accessor: "customer_id",
  },
  {
    header: "Date",
    accessor: "date",
    renderCell: (row) => formatDateMonthYear(row.date),
  },
  {
    header: "Purchase Order",
    accessor: "reportGroupLabel",
    renderCell: (row) => (
      <GenericTag variant="neutral" label={formatReportGroup(row)} size="small" />
    ),
  },
  {
    header: "Status",
    accessor: "status",
    renderCell: (row) => {
      const { variant, label } = formatReportStatus(row.status);
      return <GenericTag variant={variant} label={label} size="small" />;
    },
  },
];
